import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { peopleService } from '../../services/peopleService';
import MantleGrid, { MantleGridButton } from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';

// =============================================================================
// Job Titles — a per-version catalogue of job titles, each optionally mapped to
// a grade from bud_grades (see Grades). Wired to bud_job_titles. Global to the
// version (not site-scoped). New titles are entered on the inline line above
// the grid; grade is a dropdown of the version's grades.
// =============================================================================

const inputClass =
  'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400';

const newId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
      }));

const BLANK_ENTRY = { title: '', grade_code: '' };

const JobTitles = () => {
  const { selectedVersion } = useAppContext();
  const [titles, setTitles] = useState([]);   // [{ job_title_id, title, grade_code, isNew }]
  const [grades, setGrades] = useState([]);
  const [dirty, setDirty] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [entry, setEntry] = useState(BLANK_ENTRY);

  const loadData = useCallback(async () => {
    if (!selectedVersion) { setTitles([]); return; }
    setIsLoading(true); setError(null);
    try {
      const res = await peopleService.getJobTitles();
      if (!res.success) throw new Error(res.message);
      setTitles((res.data.jobTitles || []).map((j) => ({ ...j, isNew: false })));
      setGrades(res.data.grades || []);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load job titles.');
      setTitles([]);
    } finally { setIsLoading(false); }
  }, [selectedVersion]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = useMemo(() => ([
    { columnId: 'title', width: 280, resizable: true },
    { columnId: 'grade', width: 160, resizable: true },
    { columnId: 'del', width: 40, resizable: false },
  ]), []);

  const gridRows = useMemo(() => {
    const header = {
      rowId: 'header', height: GRID.size.headerRow,
      cells: [cell.header('Job Title'), cell.header('Grade'), cell.header('')],
    };
    const rows = titles.map((j, idx) => ({
      rowId: j.job_title_id, height: GRID.size.dataRow,
      cells: [
        cell.editText(j.title, { idx }),
        cell.editText(j.grade_code || '', { idx }),
        cell.text('✕', { idx, color: GRID.color.muted }),
      ],
    }));
    return [header, ...rows];
  }, [titles]);

  const handleChanges = (changes) => {
    setTitles((prev) => {
      const next = prev.map((j) => ({ ...j }));
      // A multi-cell paste arrives as one onCellsChanged call PER CELL in a
      // single tick. Collect this call's keys locally and merge them with a
      // functional setDirty — seeding from the `dirty` closure instead would
      // restart from the same stale base each call and only the last pasted
      // cell would survive to be saved.
      const touched = {};
      for (const ch of changes) {
        const col = String(ch.columnId);
        const j = next.find((x) => x.job_title_id === ch.rowId);
        if (!j) continue;
        if (col === 'title') { j.title = ch.newCell?.text ?? ''; touched[j.job_title_id] = true; }
        else if (col === 'grade') { j.grade_code = (ch.newCell?.text ?? '').trim(); touched[j.job_title_id] = true; }
      }
      setDirty((prevDirty) => ({ ...prevDirty, ...touched }));
      return next;
    });
  };

  const handleFocus = (loc) => {
    if (String(loc.columnId) !== 'del') return true;
    const id = String(loc.rowId);
    if (id === 'header') return true;
    deleteTitle(id);
    return false;
  };

  const setE = (k, v) => setEntry((prev) => ({ ...prev, [k]: v }));
  const entryProblem = !entry.title.trim() ? 'A job title is required.' : '';

  const addTitle = () => {
    if (entryProblem) return;
    const id = newId();
    setTitles((prev) => [...prev, { job_title_id: id, title: entry.title.trim(), grade_code: entry.grade_code || '', isNew: true }]);
    setDirty((prev) => ({ ...prev, [id]: true }));
    setEntry(BLANK_ENTRY);
  };

  const deleteTitle = async (id) => {
    const j = titles.find((x) => x.job_title_id === id);
    if (j?.isNew) {
      setTitles((prev) => prev.filter((x) => x.job_title_id !== id));
      setDirty((prev) => { const d = { ...prev }; delete d[id]; return d; });
      return;
    }
    if (!window.confirm(`Delete job title "${j?.title || ''}"?`)) return;
    setError(null);
    try { await peopleService.deleteJobTitle(id); await loadData(); }
    catch (e) { setError(e.response?.data?.message || 'Failed to delete job title.'); }
  };

  const hasChanges = Object.keys(dirty).length > 0;

  const handleSave = async () => {
    setError(null);
    const payload = titles
      .filter((j) => dirty[j.job_title_id])
      .map((j) => ({ job_title_id: j.job_title_id, title: j.title, grade_code: j.grade_code }));
    if (payload.length === 0) return;
    setIsSaving(true);
    try {
      const res = await peopleService.saveJobTitles(payload);
      if (!res.success) throw new Error(res.message);
      await loadData();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save job titles.');
    } finally { setIsSaving(false); }
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Job Titles</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          A catalogue of job titles for this budget version, each mapped to a grade from Grades. Version comes from the top bar.
        </p>
        {error && <div className="mt-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">{error}</div>}
      </div>

      {/* Inline add line */}
      {selectedVersion && (
        <div className="shrink-0 px-6 pb-3">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Job title <span className="text-red-600 dark:text-red-400">*</span>
              <input className={`${inputClass} w-64`} value={entry.title} onChange={(e) => setE('title', e.target.value)} placeholder="e.g. Machine Operator" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Grade
              <select className={`${inputClass} w-40`} value={entry.grade_code} onChange={(e) => setE('grade_code', e.target.value)}>
                <option value="">(None)</option>
                {grades.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={addTitle}
              disabled={!!entryProblem}
              title={entryProblem || 'Add job title'}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white mb-0.5 ${entryProblem ? 'bg-slate-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {!selectedVersion ? (
            <div className="p-10 text-center text-sm text-slate-400">Select a budget version in the top bar to begin.</div>
          ) : (
            <MantleGrid
              rows={gridRows}
              columns={columns}
              isLoading={isLoading}
              onCellsChanged={handleChanges}
              onFocusLocationChanging={handleFocus}
              stickyBottomRows={0}
              hint="Grade must match a code from Grades (blank clears it). ✕ deletes a job title."
              toolbar={
                <MantleGridButton onClick={handleSave} disabled={isSaving || !hasChanges} accent={hasChanges}>{isSaving ? 'Saving…' : 'Save'}</MantleGridButton>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobTitles;
